import { CoreInterfaces, DTOs } from "./../../Models";
import * as StateProcessors from "../state-processors";
import * as Constants from "../../Constants/Constants";
import { applyUpdatesOnQuestionChange } from "src/utils/processors/Questions";
import {
  handleAnnualPayrollRoutinesState,
  handleAnnualReportingListedCompaniesState,
  handleAnnualReportingState,
  handleEngagementCounselingState,
  handleEngagementMetadataResetWhenConfigurationChange,
  handleInterimListedCompaniesState,
  handleValidFromDateChange,
  handleYearlyServiceState,
  setEngagementConfigurationOwner,
  applyChangesTimestampOnNextState,
  handleAuditServices,
  handleTaxEngagementLetters,
  handleExtraServiceState,
  isFinalorOutdatedVersion,
  setNotSignedEmployeesUIDs,
} from "src/utils";

export function orchestrationReducer(
  state: CoreInterfaces.AppState,
  action: { type: Constants.AppStateActions; payload?: any }
): CoreInterfaces.AppState {
  switch (action.type) {
    case Constants.AppStateActions.UpdateStateWithSavedConfiguration: {
      const actionPayload = action.payload as Partial<CoreInterfaces.AppState>;
      const nextState = {
        ...state,
        currentConfiguration: {
          ...state.currentConfiguration,
          ...actionPayload.currentConfiguration,
          applicationConfiguration: {
            ...state.currentConfiguration.applicationConfiguration,
            ...actionPayload.currentConfiguration.applicationConfiguration,
            subsidiaryConfiguration:
              state.currentConfiguration.applicationConfiguration
                .subsidiaryConfiguration,
          },
        },
        remoteData: {
          ...state.remoteData,
          ...actionPayload.remoteData,
        },
      };
      if (!!actionPayload.services) {
        const services = actionPayload.services.map((service) => {
          applyUpdatesOnQuestionChange(state, service);
          return service;
        });
        const generalInformation = services.find(
          (service) =>
            service.data.code === Constants.ServiceCode.GeneralInformation
        );
        const generalVariables =
          StateProcessors.createGeneralVariablesObject(generalInformation);

        generalInformation.data.questions.forEach((eachGeneralQuestion) => {
          if (
            eachGeneralQuestion.data.code ===
            Constants.GeneralInformationQuestion.QT105
          ) {
            setNotSignedEmployeesUIDs(nextState, eachGeneralQuestion);
          }
          StateProcessors.updateServicesOnGeneralQuestionChange(
            services,
            eachGeneralQuestion,
            generalVariables
          );
        });

        handleAnnualReportingListedCompaniesState(services);
        handleInterimListedCompaniesState(services);
        handleAnnualReportingState(services);
        handleEngagementCounselingState(services);
        handleExtraServiceState(services, isFinalorOutdatedVersion(nextState));
        handleAuditServices(services);
        handleTaxEngagementLetters(services);

        nextState.generalVariables = generalVariables;
        nextState.services = services.map((service) => {
          applyUpdatesOnQuestionChange(
            {
              ...nextState,
              services,
            },
            service
          );

          return service;
        });
      }

      return nextState;
    }
    case Constants.AppStateActions.UpdateStateWithImportedConfiguration: {
      const actionPayload = action.payload as Partial<CoreInterfaces.AppState>;

      const nextState: CoreInterfaces.AppState = {
        ...state,
        services: actionPayload.services,
        currentConfiguration: {
          ...state.currentConfiguration,
          ...actionPayload.currentConfiguration,
          applicationConfiguration: {
            ...state.currentConfiguration.applicationConfiguration,
            ...actionPayload.currentConfiguration.applicationConfiguration,
            subsidiaryConfiguration:
              state.currentConfiguration.applicationConfiguration
                .subsidiaryConfiguration,
          },
        },
        remoteData: {
          ...state.remoteData,
          ...actionPayload.remoteData,
        },
        unsavedChangesTimestamp: null,
      };

      if (!!actionPayload.services) {
        const generalInformation = actionPayload.services.find(
          (service) =>
            service.data.code === Constants.ServiceCode.GeneralInformation
        );
        const generalVariables =
          StateProcessors.createGeneralVariablesObject(generalInformation);
        nextState.generalVariables = generalVariables;

        const services = actionPayload.services.map((service) => {
          applyUpdatesOnQuestionChange(nextState, service);
          return service;
        });

        generalInformation.data.questions.forEach((eachGeneralQuestion) => {
          if (
            eachGeneralQuestion.data.code ===
            Constants.GeneralInformationQuestion.QT105
          ) {
            setNotSignedEmployeesUIDs(nextState, eachGeneralQuestion);
          }
          StateProcessors.updateServicesOnGeneralQuestionChange(
            services,
            eachGeneralQuestion,
            generalVariables
          );
        });

        handleAnnualReportingListedCompaniesState(services);
        handleInterimListedCompaniesState(services);
        handleAnnualReportingState(services);
        handleEngagementCounselingState(services);
        handleExtraServiceState(services, isFinalorOutdatedVersion(nextState));
        handleAuditServices(services);
        handleTaxEngagementLetters(services);

        nextState.generalVariables = generalVariables;
        nextState.services = services.map((service) => {
          applyUpdatesOnQuestionChange(
            {
              ...nextState,
              services,
            },
            service
          );

          return service;
        });
      }

      return nextState;
    }
    case Constants.AppStateActions.UpdateRemoteData: {
      const actionPayload = action.payload as Partial<CoreInterfaces.AppState>;
      const services = state.services.map((service) => {
        applyUpdatesOnQuestionChange(
          {
            ...state,
            remoteData: {
              ...state.remoteData,
              ...actionPayload.remoteData,
            },
          },
          service
        );

        return service;
      });
      const generalInformation = services.find(
        (service) =>
          (service.data.code = Constants.ServiceCode.GeneralInformation)
      );
      const generalVariables =
        StateProcessors.createGeneralVariablesObject(generalInformation);
      const nextState = {
        ...state,
        generalVariables,
        services,
        remoteData: {
          ...state.remoteData,
          ...actionPayload.remoteData,
        },
      };
      return nextState;
    }
    case Constants.AppStateActions.HideSystemAdministrationMessage: {
      const systemAdministrationMessageId = action.payload as string;

      const systemAdministrationMessages =
        state.remoteData.systemAdministrationMessages.map((item) => {
          if (item.id === systemAdministrationMessageId) {
            item.state.isShown = false;
          }

          return item;
        });

      const nextState = {
        ...state,
        remoteData: {
          ...state.remoteData,
          systemAdministrationMessages,
        },
      };
      return nextState;
    }
    case Constants.AppStateActions.UpdateApplicationConfiguration: {
      const applicationConfigurationReadPack =
        action.payload as CoreInterfaces.ApplicationConfigurationReadPack;
      const nextServices =
        StateProcessors.updateServicesOnApplicationConfigurationChange(
          state.services,
          applicationConfigurationReadPack
        );
      const applicationConfigurationPart: CoreInterfaces.ApplicationConfiguration =
        {
          rateCartConfiguration:
            applicationConfigurationReadPack.RateCartConfiguration,
          taskAllocationConfigurationVersion:
            applicationConfigurationReadPack.TaskAllocationConfiguration
              .Version,
          taskAllocationConfigurationDescription:
            applicationConfigurationReadPack.TaskAllocationConfiguration
              .Description,
          taskCostConfigurationVersion:
            applicationConfigurationReadPack.TaskCostConfiguration.Version,
          taskCostConfigurationDescription:
            applicationConfigurationReadPack.TaskCostConfiguration.Description,
          subsidiaryConfiguration:
            applicationConfigurationReadPack.SubsidiaryConfiguration,
        };

      const nextState: CoreInterfaces.AppState = {
        ...state,
        services: nextServices,
        currentConfiguration: {
          ...state.currentConfiguration,
          applicationConfiguration: applicationConfigurationPart,
        },
      };
      if (
        !!applicationConfigurationReadPack.TaskAllocationConfiguration
          .TaskAllocations
      ) {
        nextState.taskAllocations =
          applicationConfigurationReadPack.TaskAllocationConfiguration.TaskAllocations;
      }
      if (!!applicationConfigurationReadPack.TaskCostConfiguration.TaskCosts) {
        nextState.taskCosts =
          applicationConfigurationReadPack.TaskCostConfiguration.TaskCosts;
      }
      if (!!applicationConfigurationReadPack.SubsidiaryConfiguration) {
        nextState.currentConfiguration.applicationConfiguration.subsidiaryConfiguration =
          applicationConfigurationReadPack.SubsidiaryConfiguration;
      }
      return nextState;
    }
    case Constants.AppStateActions.QuestionUpdated: {
      const serviceDTO = action.payload.serviceDTO as DTOs.ServiceDTO;
      const modifiedQuestion = action.payload.questionDTO as DTOs.QuestionDTO;
      const previousState = structuredClone(state);

      let generalVariables = state.generalVariables;
      if (serviceDTO.data.code === Constants.ServiceCode.GeneralInformation) {
        generalVariables = StateProcessors.createGeneralVariablesObject(
          serviceDTO,
          modifiedQuestion
        );
      }
      const intermediateState = {
        ...state,
        generalVariables,
      };

      const processedServices =
        StateProcessors.updateServicesBasedOnQuestionUpdate(
          intermediateState,
          serviceDTO,
          modifiedQuestion
        );

      handleAnnualReportingState(processedServices);

      let nextState: CoreInterfaces.AppState = {
        ...intermediateState,
        services: processedServices,
      };

      applyChangesTimestampOnNextState(nextState);

      if (
        modifiedQuestion.data.code ===
          Constants.GeneralInformationQuestion.Q0003 ||
        modifiedQuestion.data.code ===
          Constants.GeneralInformationQuestion.QA0006 ||
        modifiedQuestion.data.code ===
          Constants.GeneralInformationQuestion.QT104
      ) {
        setEngagementConfigurationOwner(nextState, modifiedQuestion);
      }

      if (
        modifiedQuestion.data.code ===
        Constants.GeneralInformationQuestion.QT105
      ) {
        setNotSignedEmployeesUIDs(nextState, modifiedQuestion);
      }

      nextState = handleValidFromDateChange(nextState);
      handleEngagementMetadataResetWhenConfigurationChange(
        previousState,
        nextState
      );

      return nextState;
    }
    case Constants.AppStateActions.ServiceUpdated: {
      const nextService = action.payload as DTOs.ServiceDTO;
      const nextServices = state.services.map((eachService) => {
        if (eachService.data.code === nextService.data.code) {
          return nextService;
        } else {
          return eachService;
        }
      });
      const previousState = structuredClone(state);

      handleYearlyServiceState(nextServices);
      handleAnnualPayrollRoutinesState(nextServices);
      handleAnnualReportingListedCompaniesState(nextServices);
      handleInterimListedCompaniesState(nextServices);
      handleAnnualReportingState(nextServices);
      handleEngagementCounselingState(nextServices);
      handleExtraServiceState(nextServices, isFinalorOutdatedVersion(state));
      handleAuditServices(nextServices);
      handleTaxEngagementLetters(nextServices);

      const nextState: CoreInterfaces.AppState = {
        ...state,
        services: nextServices,
      };

      applyChangesTimestampOnNextState(nextState);

      // TODO to improve that
      [
        Constants.ServiceCode.GeneralInformation,
        Constants.ServiceCode.PayrollAndExpenseAndTravelInvoiceManagement,
        Constants.ServiceCode.AnnualReporting,
        Constants.ServiceCode.OtherAccountAndReconciliation,
        Constants.ServiceCode.PeriodReporting,
      ].forEach((serviceCode) => {
        applyUpdatesOnQuestionChange(
          nextState,
          state.services.find(
            (eachService) => eachService.data.code === serviceCode
          )
        );
      });
      const intermediateState = handleValidFromDateChange(nextState);
      handleEngagementMetadataResetWhenConfigurationChange(
        previousState,
        intermediateState
      );
      return intermediateState;
    }
    default:
      return state;
  }
}
